/**
 * **「주문 고객」(전화·QR 주문 고객) 화면의 판정·문구 한 곳** — 의존성 없는 순수 모듈이다(Vue·Element·`@/` 별칭 금지 — 단위 시험이
 * node 에서 그대로 읽는다). 요청·창은 화면(`views/TelInfoView.vue`·`components/telInfo/*`, 경계)이 한다.
 *
 * 세 가지를 답한다:
 *
 * 1. **고객 수정은 바뀐 칸만 보낸다**([telInfoPatchOf]) — `PUT /telInfo/{seq}` 는 본문에 키가 있는 칸만 바꾼다(appserver AP1). 받은
 *    전체를 되돌려 보내면 그 사이 주문이 쓴 거울 주소·주문 수를 옛 값으로 덮는다. 화면이 고치지 않는 칸(`addr`·`addrDtl`·
 *    `useYn` — 거울 주소는 고객 기록 처리기가 배달 주문 때 쓴다)은 **어떤 경우에도** 싣지 않는다.
 * 2. **취향은 매장 주방 요청 문구에서만 고른다**(결정 21 — [phraseOptionsOf]). 요청 문구 편집(`GET`·`PUT /telInfo/phrases`)은
 *    종류(주방·라이더)마다 목록을 통째로 바꾼다 — 두 종류를 늘 함께 보낸다([phrasesBodyOf]).
 * 3. **주소록**(`tbl_tel_info_address`)은 보기와 최소 고치기(별칭·상세·라이더 요청·숨기기)만 — 새 주소·건물 찾기는 POS 몫이다.
 *    합쳐 숨긴 주소(`mergedInto`)는 「합쳐짐」으로 보이고 고치기(숨김 풀기)를 두지 않는다 — 남긴 주소로 안내한다([addressRowsOf]).
 *
 * 계약 정본: `ordern-workspace/docs/planning/pos2lite-phone-order-impl-20261002/wp-w1-admin-web-brief.md` §1-0 V5~V7 · §1-4
 * (준비 문서 §3-1 이 AP2 에 건 관리자 세션 API 두 묶음) · core §b-6(요청 문구 모양)·§d-2(관리자 PUT).
 */
// 확장자를 적는 이유: 단위 시험이 앱 번들 밖(node)에서 이 파일을 그대로 읽는다 — node ESM 은 확장자를 추론하지 않는다
import { numberText } from './formatterNum.js'
import { formatKstIso } from './kstDate.js'

/** 「주문 고객」 라우트 — 사이드 메뉴 행(`sys_resources.url`)과 같은 글자다 */
export const TEL_INFO_LIST_PATH = '/v/business/telInfo/list'

/** 고객 CRUD base */
export const TEL_INFO_API = '/telInfo'

/** 요청 문구(V5) — `storeCd` 를 싣지 않으면 로그인 매장이다(화면은 싣지 않는다) */
export const TEL_INFO_PHRASES_API = '/telInfo/phrases'

/** 한 고객의 주소록(V6) */
export function telInfoAddressesUrl(seq) {
  return `${TEL_INFO_API}/${seq}/addresses`
}

/** 주소 하나 고치기(V6) */
export function telInfoAddressUrl(addressId) {
  return `${TEL_INFO_API}/addresses/${addressId}`
}

/**
 * 세션 액션 id — appserver 권한 SQL(054, AP2)의 `sys_action.action_id` 와 같은 글자다(V3). 화면 게이트가 이것만 본다: 액션이
 * 없으면 부르지도 그리지도 않는다(403 은 이 앱에서 강제 로그아웃). 그래서 서버 행이 나가기 전에도 기존 화면은 그대로다.
 */
export const TEL_INFO_ACTION = Object.freeze({
  /** `GET /telInfo/phrases` */
  PHRASE_GET: 'TelInfoPhraseGet',
  /** `PUT /telInfo/phrases` */
  PHRASE_UP: 'TelInfoPhraseUp',
  /** `GET /telInfo/* /addresses` */
  ADDRESS_LIST: 'TelInfoAddressList',
  /** `POST /telInfo/addresses/*` */
  ADDRESS_UP: 'TelInfoAddressUp',
})

/**
 * 칸 길이 — 서버 검증·표 칸과 같은 값이다(이름 30·메모 255 = AP1 `TelInfo`, 상세 100·별칭 20·라이더 요청 합 250 = 주소록 표,
 * 문구 50·종류당 20 = `tbl_store_request_phrase` · V5). Element 의 `maxlength` 는 UTF-16 단위라 서버(코드 포인트)보다 엄격하다
 * — 안전한 쪽이라 맞추지 않는다.
 */
export const TEL_INFO_LIMITS = Object.freeze({
  name: 30,
  memo: 255,
  addrDtl: 100,
  label: 20,
  riderMemo: 250,
  phrase: 50,
  phrasesPerKind: 20,
})

/** 요청 문구의 종류(V5 본문 키) */
export const PHRASE_KIND = Object.freeze({ KITCHEN: 'kitchen', RIDER: 'rider' })

/** 요청 문구의 출처 — 매장 행이 없으면 서버 기본 목록(`DEFAULT`) */
export const PHRASE_SOURCE = Object.freeze({ STORE: 'STORE', DEFAULT: 'DEFAULT' })

/** 「기본 문구로 되돌리기」 본문 — `null` 이 그 종류의 매장 행을 지운다(V5). 두 종류를 함께 */
export const PHRASES_RESET_BODY = Object.freeze({ kitchen: null, rider: null })

/** 주소 별칭(V6) — 「없음」은 빈 값이다 */
export const ADDRESS_LABELS = Object.freeze(['집', '회사'])

/** 주소록 출처 중 가져온 것 */
const IMPORTED_SOURCE = 'CUSTOMER_IMPORT'

/** 화면 문구 */
export const TEL_INFO_TEXT = Object.freeze({
  importButton: '고객 가져오기',
  phrasesButton: '요청 문구',

  memoPlaceholder: '고객에 대한 메모를 적어주세요.',
  /** 칸 아래 늘 보이는 줄 — 메모가 있으면 placeholder 가 안 보이므로 안내를 거기에만 두지 않는다(결정 27) */
  memoNotice: '매장 POS 직원에게도 보여요. 주문서·배달대행사로는 가지 않아요.',

  prefLabel: '취향',
  prefPlaceholder: '고르지 않음',
  prefHelp: '매장 주방 요청 문구에서 골라요. 전화주문 때 주방 요청에 ★로 들어가요. 문구에 없는 취향은 메모에 적어요.',
  prefMissing: '지금 문구에 없음',
  editPhrases: '요청 문구 고치기',
  priorLead: '쓰던 프로그램',

  phraseDialogTitle: '요청 문구',
  kitchenTitle: '주방 요청 문구',
  kitchenHelp: 'POS 주방 요청 칩과 고객 취향으로 써요. 주문서에 그대로 찍혀요.',
  riderTitle: '라이더 요청 문구',
  riderHelp: '주소마다 라이더 요청 칩으로 써요. 배달대행사로 가요.',
  defaultNotice: '오더엔 기본 문구를 쓰고 있어요. 고쳐서 저장하면 이 매장 문구가 돼요.',
  keptNotice: '지운 문구라도 고객에게 이미 든 취향·라이더 요청은 남아요.',
  phrasePlaceholder: '새 문구',
  addPhrase: '더하기',
  removePhrase: '빼기',
  phraseEmpty: '문구를 적어 주세요',
  phraseTooLong: '문구는 50자까지예요',
  phraseDuplicate: '이미 있는 문구예요',
  phraseTooMany: '종류마다 20개까지예요',
  phrasesSaved: '요청 문구를 저장했어요. POS는 다음에 로그인하거나 매장을 바꿀 때 새 문구를 받아요.',
  phrasesLoadFailed: '요청 문구를 읽지 못했어요.',
  phrasesSaveFailed: '요청 문구를 저장하지 못했어요.',
  resetDefault: '기본 문구로 되돌리기',
  resetConfirm: '이 매장 문구를 지우고 기본 문구로 돌아가요',

  addressBookTitle: '주소록',
  addressEmpty: '저장된 주소가 없어요. 주소는 전화·QR 주문을 접수할 때 더해져요.',
  addressNeedsBuilding: '건물 확인 전 — 첫 전화 때 POS에서 건물 찾기',
  addressHidden: '숨김',
  /** 합쳐 숨긴 주소의 꼬리표 — 사용자가 숨긴 주소(「숨김」)와 가른다 */
  addressMergedTag: '합쳐짐',
  /**
   * 합쳐 숨긴 주소의 줄 — 남긴 주소를 이름(별칭, 없으면 주소 글자)으로 가리키고, 주소록에 없으면 「다른 주소」. 이름 뒤에는 받침과
   * 무관한 조사(「에」)만 붙인다
   */
  addressMergedInto: (name) => `${name ? `‘${name}’` : '다른 주소'}에 합쳐졌어요`,
  /** 합쳐 숨긴 주소의 고치기 자리 — 고치기는 남긴 주소의 줄에서 한다 */
  addressMergedEdit: '남긴 주소를 고쳐요',
  addressImported: '가져온 주소',
  addressEdit: '고치기',
  addressDialogTitle: '주소 고치기',
  addressLabel: '별칭',
  addressLabelNone: '없음',
  addressDetail: '상세 주소',
  addressRider: '라이더 요청',
  addressHide: '숨기기',
  addressHideHelp: 'POS 주소 목록에서 숨겨요',
  addressMerged: '같은 주소가 있어 하나로 합쳤어요',
  addressSaved: '주소를 고쳤어요',
  addressLoadFailed: '주소록을 읽지 못했어요.',
  addressSaveFailed: '주소를 고치지 못했어요.',
})

/** 요청 문구 창의 두 목록 — 종류(V5 본문 키)·제목·쓰임 */
export const PHRASE_KINDS = Object.freeze([
  Object.freeze({ key: PHRASE_KIND.KITCHEN, title: TEL_INFO_TEXT.kitchenTitle, help: TEL_INFO_TEXT.kitchenHelp }),
  Object.freeze({ key: PHRASE_KIND.RIDER, title: TEL_INFO_TEXT.riderTitle, help: TEL_INFO_TEXT.riderHelp }),
])

/** 고객 수정이 보내는 키 여섯(V7) — 이 밖의 키는 보내지 않는다 */
export const TEL_INFO_PATCH_KEYS = Object.freeze([
  'name',
  'lastShopMemo',
  'prefTags',
  'marketingAgreeDate',
  'orderCnt',
  'telOrderCnt',
])

// ─── 비교 ─────────────────────────────────────────────────────────────────────────────────────────────

/** 글자 칸의 값 — 비었거나 공백뿐이면 `null`(「비움」) */
const textOf = (value) => (value == null || String(value).trim() === '' ? null : String(value))

/** 목록 칸의 값 — 배열이 아니면 빈 목록 */
const listOf = (value) => (Array.isArray(value) ? value : [])

/** 두 목록이 같은가 — **순서까지**(취향·라이더 요청의 순서가 곧 칩 순서다) */
const sameList = (a, b) => a.length === b.length && a.every((item, index) => item === b[index])

/** 숫자 칸의 값 */
const countOf = (value) => (value == null || value === '' ? 0 : Number(value))

/** 칸 하나의 비교 규칙 — 같은지와 보낼 값 */
const FIELD_RULES = Object.freeze({
  name: { same: (a, b) => textOf(a) === textOf(b), wire: textOf },
  lastShopMemo: { same: (a, b) => textOf(a) === textOf(b), wire: textOf },
  prefTags: { same: (a, b) => sameList(listOf(a), listOf(b)), wire: (value) => listOf(value).slice() },
  marketingAgreeDate: { same: (a, b) => textOf(a) === textOf(b), wire: textOf },
  orderCnt: { same: (a, b) => countOf(a) === countOf(b), wire: countOf },
  telOrderCnt: { same: (a, b) => countOf(a) === countOf(b), wire: countOf },
})

/**
 * **고객 수정 본문 — 화면에서 바뀐 칸만**(V7). `before` 는 수정 창을 열 때 받은 고객(`GET /telInfo/{seq}`), `form` 은 지금 창의 값.
 * 바뀐 칸이 없으면 `{}` — 화면은 요청 없이 닫는다. 빈 글자는 `null`(비움)로 보낸다. 순수 — 두 인자는 그대로다.
 *
 * 취향은 창이 그 칸을 그렸을 때만 바뀔 수 있다 — 요청 문구를 못 읽어 칸을 그리지 않았으면 `form` 의 값이 받은 그대로라 싣지 않는다.
 *
 * @returns {object} [TEL_INFO_PATCH_KEYS] 중 바뀐 키만
 */
export function telInfoPatchOf(before, form) {
  const loaded = before || {}
  const edited = form || {}

  return TEL_INFO_PATCH_KEYS.filter((key) => !FIELD_RULES[key].same(loaded[key], edited[key])).reduce(
    (patch, key) => Object.assign(patch, { [key]: FIELD_RULES[key].wire(edited[key]) }),
    {}
  )
}

/**
 * 「쓰던 프로그램: 주문 23번 · 312,000원 · 마지막 2026-09-20」 — 가져온 이전 값(읽기 전용, PUT 이 받지 않는다). 셋 다 없으면
 * `null`. 오더엔 주문 수와 섞지 않는다.
 */
export function priorLineOf(customer) {
  const item = customer || {}
  const parts = [
    item.priorOrderCnt != null ? `주문 ${numberText(item.priorOrderCnt)}번` : null,
    item.priorTotalAmt != null ? `${numberText(item.priorTotalAmt)}원` : null,
    typeof item.priorLastOrderAt === 'string' && item.priorLastOrderAt
      ? `마지막 ${item.priorLastOrderAt.slice(0, 10)}`
      : null,
  ].filter(Boolean)

  return parts.length ? `${TEL_INFO_TEXT.priorLead}: ${parts.join(' · ')}` : null
}

// ─── 요청 문구 (V5) ───────────────────────────────────────────────────────────────────────────────────

/**
 * 고르기 상자의 항목 — 매장 문구 순서 그대로 + **지금 든 값 중 문구에 없는 것**(「지금 문구에 없음」)을 뒤에 남긴다. 서버는 든
 * 취향·라이더 요청을 문구와 대조하지 않으므로(core §b-5) 문구를 지워도 값은 남는다 — 그 값을 고르기 상자가 지우지 않게 한다.
 * 주방 문구(취향)와 라이더 문구(주소의 라이더 요청)가 같이 쓴다.
 *
 * @param {Array<string>} phrases 매장 문구(그 종류)
 * @param {Array<string>} current 지금 든 값
 * @returns {Array<{value: string, label: string, missing: boolean}>}
 */
export function phraseOptionsOf(phrases, current) {
  const known = listOf(phrases)
  const missing = listOf(current).filter((value) => known.indexOf(value) < 0)

  return known
    .map((value) => ({ value, label: value, missing: false }))
    .concat(missing.map((value) => ({ value, label: `${value} (${TEL_INFO_TEXT.prefMissing})`, missing: true })))
}

/** 받은 요청 문구(V5 응답) → 편집 초안(두 목록 사본) */
export function phraseDraftOf(view) {
  const item = view || {}
  return {
    kitchen: listOf(item.kitchen).slice(),
    rider: listOf(item.rider).slice(),
    source: item.source || PHRASE_SOURCE.DEFAULT,
  }
}

/** 저장 본문(V5 PUT) — **두 종류를 늘 함께**(출처가 매장에 하나라 한 종류만 매장 행이 되는 상태를 만들지 않는다), 앞뒤 공백을 다듬는다 */
export function phrasesBodyOf(draft) {
  const item = draft || {}
  return {
    kitchen: listOf(item.kitchen).map((text) => String(text).trim()),
    rider: listOf(item.rider).map((text) => String(text).trim()),
  }
}

/** 겹침 판정의 꼴 — 공백을 무시한다(서버 `UNIQUE(store_cd, kind, text)` 앞의 다듬기와 같은 뜻) */
const squashed = (text) => String(text).replace(/\s+/g, '')

/**
 * 문구 하나 더하기 — 빈 값·50자 초과·겹침(공백 무시)·종류당 20개 초과면 목록은 그대로이고 그 칸 아래 한 줄(`error`)이다. 순수.
 *
 * @returns {{list: Array<string>, error: ?string}}
 */
export function addPhrase(list, text) {
  const items = listOf(list)
  const value = String(text == null ? '' : text).trim()

  if (!value) return { list: items, error: TEL_INFO_TEXT.phraseEmpty }
  if (Array.from(value).length > TEL_INFO_LIMITS.phrase) return { list: items, error: TEL_INFO_TEXT.phraseTooLong }
  if (items.some((item) => squashed(item) === squashed(value))) {
    return { list: items, error: TEL_INFO_TEXT.phraseDuplicate }
  }
  if (items.length >= TEL_INFO_LIMITS.phrasesPerKind) return { list: items, error: TEL_INFO_TEXT.phraseTooMany }

  return { list: items.concat([value]), error: null }
}

/** 문구 하나를 위(`-1`)·아래(`+1`)로 — 끝을 넘으면 그대로. 새 배열 */
export function movePhrase(list, index, delta) {
  const items = listOf(list)
  const target = index + delta
  if (index < 0 || index >= items.length || target < 0 || target >= items.length) return items.slice()

  return items.map((item, at) => (at === index ? items[target] : at === target ? items[index] : item))
}

/** 문구 하나 빼기 — 새 배열 */
export function removePhrase(list, index) {
  return listOf(list).filter((item, at) => at !== index)
}

// ─── 주소록 (V6) ──────────────────────────────────────────────────────────────────────────────────────

/** 주소 글자 — 건물이면 건물 이름(없으면 짧은 도로명) + 상세, 글자 주소면 그 글자(도로명, 없으면 지번) + 상세 */
function addressTextOf(address) {
  const place = address.place
  const join = (parts) => parts.filter(Boolean).join(' ')

  return place
    ? join([place.name || place.roadShort, address.addrDtl])
    : join([address.roadAddress || address.jibunAddress, address.addrDtl])
}

/**
 * 합쳐 숨긴 주소가 가리키는 남긴 주소의 이름 — 같은 주소록([book] — 관리자 주소록은 숨긴 것까지 다 받는다)에서 id 로 찾아 별칭, 없으면
 * 주소 글자. 주소록에 없으면 `null`(「다른 주소」).
 */
function keptNameOf(address, book) {
  const kept = book.find((other) => other && other.addressId === address.mergedInto)
  return kept ? kept.label || addressTextOf(kept) : null
}

/** 주소록 한 줄 — [book] 은 같은 주소록 전체([addressRowsOf]) */
function addressRowOf(item, book) {
  const address = item || {}
  const place = address.place
  const rider = listOf(address.riderMemo)
  const last = formatKstIso('M월 d일', address.lastUsedAt)
  // 합쳐 숨긴 주소 — 서버가 합친 표지를 끝까지 따라간 남긴 주소 id 를 싣는다. 사용자가 숨긴 주소·옛 서버(칸 없음)는 아니다
  const merged = address.mergedInto != null

  return {
    addressId: address.addressId,
    label: address.label || null,
    text: addressTextOf(address),
    sub: place ? place.roadAddress || null : null,
    // 합쳐 숨긴 주소는 POS 주소 목록에 없고 그 주소의 건물 찾기는 남긴 주소가 받는다 — 그 줄에서 할 일이 아니다
    needsBuilding: !!address.needsBuilding && !merged,
    riderText: rider.length ? `라이더: ${rider.join(' · ')}` : null,
    usageText: `${numberText(address.useCount || 0)}번${last ? ` · 마지막 ${last}` : ''}`,
    hidden: !!address.hidden,
    hiddenTag: merged ? TEL_INFO_TEXT.addressMergedTag : address.hidden ? TEL_INFO_TEXT.addressHidden : null,
    mergedText: merged ? TEL_INFO_TEXT.addressMergedInto(keptNameOf(address, book)) : null,
    editable: !merged,
    imported: address.source === IMPORTED_SOURCE,
  }
}

/**
 * **주소록의 줄들** — 건물이면 건물 이름(없으면 짧은 도로명) + 상세, 아래 도로명 주소. 글자 주소면 그 글자 + 상세, 아래 「건물 확인 전 —
 * 첫 전화 때 POS에서 건물 찾기」(건물은 POS 가 찾는다).
 *
 * 숨긴 주소는 두 가지다: 사용자가 숨긴 주소는 「숨김」이고 고치기 창에서 숨김을 풀 수 있다. **합쳐 숨긴 주소**(`mergedInto` — 같은 세대라
 * 서버가 하나로 합쳤다)는 「합쳐짐」이고 「‘{남긴 주소}’에 합쳐졌어요」로 남긴 주소를 가리키며 **고치기가 없다**(`editable: false`) — 숨김을
 * 풀면 같은 세대가 다시 두 줄로 갈리고, 별칭·상세·라이더 요청을 그 줄에서 고치면 서버는 남긴 주소를 고치는데 창은 숨긴 줄의 값과 견줘
 * 바뀐 칸을 보낸다(라이더 요청은 목록째 바뀐다). 고치기는 남긴 주소의 줄에서 한다.
 */
export function addressRowsOf(items) {
  const book = listOf(items)
  return book.map((item) => addressRowOf(item, book))
}

/** 별칭 고르기 상자 — 집 · 회사 · (지금 값이 그 밖이면 그 값) · 없음(빈 값) */
export function addressLabelOptionsOf(current) {
  const extra = current && ADDRESS_LABELS.indexOf(current) < 0 ? [current] : []

  return ADDRESS_LABELS.concat(extra)
    .map((value) => ({ value, label: value }))
    .concat([{ value: '', label: TEL_INFO_TEXT.addressLabelNone }])
}

/** 고치기 창의 처음 값 — 받은 주소의 사본 */
export function addressFormOf(item) {
  const address = item || {}
  return {
    label: address.label || '',
    addrDtl: address.addrDtl || '',
    riderMemo: listOf(address.riderMemo).slice(),
    hidden: !!address.hidden,
  }
}

/**
 * 주소 고치기 본문(V6) — 바뀐 칸만. 키 없음 = 안 바꿈, `null` = 비움(별칭·상세·라이더 요청 — 라이더 요청을 다 빼면 빈 목록이 아니라
 * `null` 이다), 숨기기는 참거짓.
 */
export function addressPatchOf(before, form) {
  const loaded = before || {}
  const edited = form || {}
  const riderBefore = listOf(loaded.riderMemo)
  const riderAfter = listOf(edited.riderMemo)

  return Object.assign(
    {},
    textOf(loaded.label) !== textOf(edited.label) ? { label: textOf(edited.label) } : null,
    textOf(loaded.addrDtl) !== textOf(edited.addrDtl) ? { addrDtl: textOf(edited.addrDtl) } : null,
    sameList(riderBefore, riderAfter) ? null : { riderMemo: riderAfter.length ? riderAfter.slice() : null },
    !!loaded.hidden !== !!edited.hidden ? { hidden: !!edited.hidden } : null
  )
}
